import React, { useCallback, useState } from "react";
import Card from "@dtci/spellbook/Card";
import Icon from "@dtci/spellbook/Icon";
import Badge from "@dtci/spellbook/Badge";
import Button from "@dtci/spellbook/Button";
import Skeleton from "@dtci/spellbook/Skeleton";
import { downloadDeliveryForms, downloadAllDeliveryForms } from "ppt/api";
import { usePPTState, usePPTActions } from "tap/store/ppt/usePPT";
import { usePPTToasts } from "../../hooks/usePPTToasts";
import { PPTSeverityBadge } from "../PPTSeverityBadge";
import styles from "./styles.module.scss";

// Reserved `client` scope token for the rows-without-client-identity delivery
// (mirrors the backend's UNASSIGNED_CLIENT_KEY).
export const UNASSIGNED_CLIENT_KEY = "unassigned";

const DOWNLOAD_ALL_KEY = "__all__";

/**
 * The Deliveries card: one row per delivery destination derived from the
 * promotion's per-client discounts (GET /deliveries). Each row shows the same
 * severity circle the Discount Details grid uses (highest open-issue severity
 * on the delivery's client discounts) and an icon-only scoped download; the
 * header carries the download-all action. Rows are flat — a slide-out detail
 * panel on row click is planned for a later ticket.
 */
export const PPTDeliveriesPanel = ({ promotionId }) => {
  const { promotionDeliveries, promotionDeliveriesLoading } = usePPTState();
  const { openDeliveryDrawer } = usePPTActions();
  const { showErrorToast } = usePPTToasts();
  // clientKey (or reserved token) of the download in flight
  const [downloadingKey, setDownloadingKey] = useState(null);

  const handleDownloadDelivery = useCallback(
    async (delivery) => {
      const key = delivery.clientKey ?? UNASSIGNED_CLIENT_KEY;
      setDownloadingKey(key);
      try {
        await downloadDeliveryForms(
          promotionId,
          delivery.clientKey,
          `${delivery.label}-forms.xlsx`
        );
      } catch (error) {
        console.error("Failed to download delivery forms:", error);
        showErrorToast(`Failed to download ${delivery.label} forms. Please try again.`);
      } finally {
        setDownloadingKey(null);
      }
    },
    [promotionId, showErrorToast],
  );

  const handleDownloadAll = useCallback(
    async () => {
      setDownloadingKey(DOWNLOAD_ALL_KEY);
      try {
        await downloadAllDeliveryForms(promotionId, "client-forms.zip");
      } catch (error) {
        console.error("Failed to download delivery forms:", error);
        showErrorToast("Failed to download client forms. Please try again.");
      } finally {
        setDownloadingKey(null);
      }
    },
    [promotionId, showErrorToast],
  );

  const handleDeliveryClick = useCallback(
    (delivery) => {
      openDeliveryDrawer(delivery);
    },
    [openDeliveryDrawer],
  );

  const deliveries = promotionDeliveries?.deliveries || [];
  const deliveryCount = promotionDeliveries?.deliveryCount ?? deliveries.length;

  return (
    <Card>
      <div className={styles.headerRow}>
        <h4 className={styles.header}>
          <Icon iconType="fa" faPackage="fal" iconName="fa-paper-plane" />
          Deliveries
          <Badge shape="pill" size="small" color="primary" light>
            {deliveryCount}
          </Badge>
        </h4>
        <Button
          styleType="link"
          iconPlacement="before"
          iconType="fa-download"
          onClick={handleDownloadAll}
          isFetching={downloadingKey === DOWNLOAD_ALL_KEY}
          disabled={deliveries.length === 0}
          data-testid="deliveries-download-all"
        >
          Download All
        </Button>
      </div>
      <Skeleton isLoading={promotionDeliveriesLoading}>
        {deliveries.length === 0 ? (
          <div className={styles.empty} data-testid="deliveries-empty-state">
            No deliveries — no per-client discounts assembled yet.
          </div>
        ) : (
          <ul className={styles.deliveriesList}>
            {deliveries.map((delivery) => {
              const key = delivery.clientKey ?? UNASSIGNED_CLIENT_KEY;
              return (
                <li
                  key={key}
                  className={styles.deliveryRow}
                  onClick={() => handleDeliveryClick(delivery)}
                  data-testid={`delivery-row-${key}`}
                >
                  <span data-testid={`delivery-status-${key}`}>
                    <PPTSeverityBadge severity={delivery.maxSeverity} />
                  </span>
                  <Button
                    styleType="link"
                    iconOnly
                    iconType="fa-download"
                    iconTypeFetching="fa-spinner-third"
                    labelText={`Download ${delivery.label} Forms`}
                    isFetching={downloadingKey === key}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadDelivery(delivery);
                    }}
                  />
                  <span className={styles.rowTitle}>{delivery.label}</span>
                  <span className={styles.rowMeta}>
                    {delivery.rowCount} discount
                    {delivery.rowCount === 1 ? "" : "s"}
                  </span>
                  <Icon
                    iconType="fa"
                    faPackage="fas"
                    iconName="fa-arrow-up-right-from-square"
                    className={styles.openIcon}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </Skeleton>
    </Card>
  );
};

export default PPTDeliveriesPanel;
